import AddressListPanel from 'Components/Generic/Address/AddressListPanel';
import { showSuccessToast } from 'Components/Generic/BOSToasterUtil';
import CancelButton from 'Components/Generic/Buttons/CancelButton';
import StatusToggleButton from 'Components/Generic/Buttons/StatusToggleButton';
import NotesListPanel from 'Components/Generic/Notes/NotesListPanel';
import { useAlertContext } from 'Data/context/Alerts';
import { OBJECT_STATUS, user_roles } from 'Data/Enum';
import GenericTableData from 'Data/GenericTableData';
import SiteDrawerData from 'Data/SiteDrawerData';
import VendorData from 'Data/VendorData';
import DataManager from 'Lib/DataManager';
import { useStateWithRef } from 'Lib/hooks/useStateWithRef';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { isUserAuthorized } from 'Util';
import VendorInformationPanel from './VendorInformationPanel';

let DM = new DataManager();
let VendorAddressData = GenericTableData.getTableDataBus(
  'VEND_ADD',
  'vendor_addresses',
);

const VendorDetails = ({ vendorId, params, editable = false }) => {
  const [vendor, setVendor, vendorRef] = useStateWithRef({});
  const [addressIdList, setAddressIdList] = useState([]);

  const { openConfirmationDialog } = useAlertContext();

  useEffect(() => {
    DM.add(
      VendorData.registerListener('BLANK_RECORD_LOADED', onVendorLoaded),
      VendorData,
    );
    DM.add(
      VendorData.registerListener('RECORD_LOADED', onVendorLoaded),
      VendorData,
    );
    DM.add(
      VendorData.registerListener('RECORD_ACTIVATED', onVendorActivated),
      VendorData,
    );
    DM.add(
      VendorData.registerListener('RECORD_DEACTIVATED', onVendorDeactivated),
      VendorData,
    );
    return () => {
      DM.clear();
    };
  }, []);

  useEffect(() => {
    if (!vendorId && !params?.id) {
      VendorData.getBlank();
    }
  }, []);

  useEffect(() => {
    if (vendorId >= 0) {
      getVendor(vendorId);
    }
  }, [vendorId]);

  useEffect(() => {
    if (params?.id && !vendorId) {
      getVendor(params.id);
    }
  }, [params]);

  useEffect(() => {
    if (vendor.addresses?.length) {
      let idList = vendor.addresses.map((add) => add.id);
      setAddressIdList(idList);
    } else {
      setAddressIdList([]);
    }
  }, [vendor]);

  const getVendor = (id) => {
    VendorData.getRecord(id, {
      _include: 'vendor_notes,addresses',
    });
  };

  const onVendorLoaded = (payload) => {
    if (payload) {
      setVendor(payload);
    }
  };
  const onSaveAddress = (isNew, savedAddress) => {
    if (isNew) {
      VendorAddressData.createRecord({
        address_id: savedAddress.id,
        vendor_id: vendor.id,
      });
    }
    getVendor(vendor.id);
  };

  const deleteAddressLinks = (addressId) => {
    if (addressId) {
      VendorAddressData.deleteRecordsWhere({
        address_id: addressId,
        organization_id: vendor.id,
      });
    }
  };

  const onNotesUpdated = () => {
    getVendor(vendor.id);
  };

  let statusCancelButton =
    editable &&
    (vendor?.id ? (
      <StatusToggleButton
        currentStatus={vendor.status}
        onStatusToggleClicked={() => {
          statusToggleClicked();
        }}
      />
    ) : (
      <CancelButton onCancel={SiteDrawerData.close} />
    ));

  const toggleVendorStatus = () => {
    vendorRef.current?.status === OBJECT_STATUS.ACTIVE.key
      ? VendorData.deactivateRecord(vendorRef.current?.id)
      : VendorData.activateRecord(vendorRef.current?.id);
  };

  const statusToggleClicked = () => {
    let currentStatusActive =
      vendorRef.current?.status === OBJECT_STATUS.ACTIVE.key;
    openConfirmationDialog({
      onConfirm: toggleVendorStatus,
      dialogTitle: currentStatusActive
        ? 'Deactivate Vendor?'
        : 'Reactivate Vendor?',
      icon: currentStatusActive ? 'disable' : undefined,
      intent: currentStatusActive ? 'danger' : 'primary',
    });
  };

  const onVendorActivated = (payload) => {
    showSuccessToast('Vendor Reactivated');
    getVendor(payload.id);
  };

  const onVendorDeactivated = (payload) => {
    showSuccessToast('Vendor Deactivated');
    getVendor(payload.id);
  };

  let noteIdList = vendor?.vendor_notes?.map((note) => note.id) || [];

  return (
    vendor && (
      <div
        style={{
          display: 'flex',
          gap: 10,
          flexDirection: 'column',
          maxWidth: 600,
        }}>
        <VendorInformationPanel
          title='Details'
          editable={editable}
          includeCustomerInfo={isUserAuthorized({
            roles: [user_roles.ADMINISTRATOR, user_roles.VENDOR_ADMINISTRATOR],
          })}
          hideDrawerButton
        />
        {isUserAuthorized({
          roles: [user_roles.ADMINISTRATOR, user_roles.VENDOR_ADMINISTRATOR],
        }) && (
          <NotesListPanel
            notesIdList={noteIdList}
            dataIdentifier='V_NOTES'
            notesTableName='vendor_notes'
            editable={editable && (!!vendor?.id || vendor.id === 0)}
            canAddNotes={editable && (!!vendor?.id || vendor.id === 0)}
            disabled={!vendor?.id && vendor.id !== 0}
            newNoteFieldValues={{ vendor_id: vendor.id }}
            onNotesUpdated={onNotesUpdated}
          />
        )}
        <AddressListPanel
          addressIdList={addressIdList}
          deleteAddressLinks={deleteAddressLinks}
          editable={editable && (!!vendor?.id || vendor.id === 0)}
          canAddAddress={editable && (!!vendor?.id || vendor.id === 0)}
          disabled={!vendor?.id && vendor.id !== 0}
          addressType='Vendor'
          onSaveAddress={onSaveAddress}
        />
        {statusCancelButton}
      </div>
    )
  );
};

VendorDetails.propTypes = {
  vendorId: PropTypes.number,
  params: PropTypes.shape({ id: PropTypes.string }),
  editable: PropTypes.bool,
};

export default VendorDetails;
